import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { ScrollReveal } from '@/components/motion'

/**
 * One card in a row's hover strip: either a crop of the real workflow
 * builder screenshot or a small product surface drawn in HTML, so every
 * card shows the row's actual subject.
 */
export type DirectionalVisual =
  /** Crop of the workflow-builder screenshot; `pos` is a background-position. */
  | { kind: 'shot'; pos: string; label: string }
  | { kind: 'doc' }
  | { kind: 'fields' }
  | { kind: 'slide' }
  | { kind: 'library' }
  | { kind: 'swatches' }
  | { kind: 'type' }
  | { kind: 'chart' }
  | { kind: 'trend' }
  | { kind: 'audit' }

export interface DirectionalItem {
  /** Two-digit index, e.g. "01". */
  index: string
  title: string
  description: string
  href: string
  /** Band color that fills the row on hover — the reference's three. */
  color: 'blue' | 'pink' | 'purple'
  /** Up to three images that rise into the row on hover. */
  visuals?: DirectionalVisual[]
}

const FILL: Record<DirectionalItem['color'], string> = {
  blue: 'bg-[var(--color-landing-accent)]',
  pink: 'bg-[#ff00f2]',
  purple: 'bg-[var(--color-landing-secondary)]',
}

const SCREENSHOT = '/images/screenshots/workflow-builder.png'

const CAPTION = 'text-[9px] font-semibold uppercase tracking-wide text-[#5a5a5a]'

function VisualContent({ visual }: { visual: DirectionalVisual }) {
  switch (visual.kind) {
    case 'shot':
      return (
        <span className="flex h-full w-full flex-col">
          <span
            className="block flex-1 bg-white bg-no-repeat"
            style={{ backgroundImage: `url(${SCREENSHOT})`, backgroundSize: '420% auto', backgroundPosition: visual.pos }}
          />
          <span className={cn(CAPTION, 'border-t border-[#ececec] px-2.5 py-1.5')}>{visual.label}</span>
        </span>
      )
    case 'doc':
      return (
        <span className="flex h-full w-full flex-col gap-1.5 px-3.5 py-3">
          <span className="h-2 w-16 bg-[#0a0a0a]" />
          <span className="mt-1 flex flex-wrap items-center gap-1 text-[8px] leading-none text-[#5a5a5a]">
            Dear
            <span className="bg-[#e9e7ff] px-1 py-0.5 font-semibold text-[var(--color-landing-accent)]">{'{{client}}'}</span>,
          </span>
          {[100, 92, 96, 70].map((w, i) => (
            <span key={i} className="h-1 bg-[#d8d8d8]" style={{ width: `${w}%` }} />
          ))}
          <span className="flex items-center gap-1 text-[8px] leading-none text-[#5a5a5a]">
            Total
            <span className="bg-[#e9e7ff] px-1 py-0.5 font-semibold text-[var(--color-landing-accent)]">{'{{amount}}'}</span>
          </span>
        </span>
      )
    case 'fields':
      return (
        <span className="flex h-full w-full flex-col justify-center gap-1.5 px-3.5">
          {['Client', 'Date', 'Amount'].map((label, i) => (
            <span key={label} className="flex flex-col gap-1">
              <span className={CAPTION}>{label}</span>
              <span className={cn('h-3.5 border border-[#d8d8d8]', i === 1 && 'border-[var(--color-landing-accent)] bg-[#e9e7ff]')} />
            </span>
          ))}
        </span>
      )
    case 'slide':
      return (
        <span className="flex h-full w-full items-center justify-center bg-[#f7f7f7] p-3">
          <span className="flex aspect-video w-full flex-col gap-1.5 border border-[#e2e2e2] bg-white p-2.5">
            <span className="h-1.5 w-1/2 bg-[#0a0a0a]" />
            <span className="flex flex-1 items-end gap-1">
              {[45, 70, 55, 90].map((h, i) => (
                <span key={i} className="flex-1 bg-[var(--color-landing-accent)]" style={{ height: `${h}%`, opacity: 0.5 + i * 0.15 }} />
              ))}
            </span>
          </span>
        </span>
      )
    case 'library':
      return (
        <span className="grid h-full w-full grid-cols-3 grid-rows-2 gap-1.5 p-3">
          <span className="flex items-center justify-center bg-[#0a0a0a] font-[family-name:var(--font-landing-display)] text-base text-white">T</span>
          <span className="bg-[var(--color-landing-accent)]" />
          <span className="bg-[linear-gradient(135deg,#0c00ff,#b200ff)]" />
          <span className="flex items-center justify-center border border-[#e2e2e2] font-[family-name:var(--font-landing-display)] text-base text-[var(--color-landing-accent)]">T</span>
          <span className="flex items-end gap-0.5 border border-[#e2e2e2] p-1">
            {[40, 75, 55].map((h, i) => <span key={i} className="flex-1 bg-[#b200ff]" style={{ height: `${h}%` }} />)}
          </span>
          <span className="bg-[#f0f0f0]" />
        </span>
      )
    case 'swatches':
      return (
        <span className="flex h-full w-full flex-col gap-1.5 p-3">
          {[
            ['#0c00ff', 'Primary'],
            ['#b200ff', 'Secondary'],
            ['#0a0a0a', 'Ink'],
          ].map(([c, name]) => (
            <span key={c} className="flex flex-1 items-center gap-2">
              <span className="h-full w-8" style={{ backgroundColor: c }} />
              <span className="flex flex-col leading-tight">
                <span className="text-[9px] font-semibold text-[#0a0a0a]">{name}</span>
                <span className="text-[8px] uppercase text-[#898989]">{c}</span>
              </span>
            </span>
          ))}
        </span>
      )
    case 'type':
      return (
        <span className="flex h-full w-full flex-col justify-center gap-1 px-3.5">
          <span className="font-[family-name:var(--font-landing-display)] text-3xl leading-none text-[#0a0a0a]">Aa</span>
          <span className={CAPTION}>Display · Headings</span>
          <span className="mt-1 text-xl leading-none text-[#0a0a0a]">Aa</span>
          <span className={CAPTION}>Body · Text</span>
        </span>
      )
    case 'chart':
      return (
        <span className="flex h-full w-full flex-col px-3 pb-2.5 pt-2.5">
          <span className={CAPTION}>Documents / week</span>
          <span className="mt-2 flex flex-1 items-end gap-1.5">
            {[38, 52, 46, 64, 70, 88].map((h, i) => (
              <span key={i} className="flex-1 bg-[var(--color-landing-accent)]" style={{ height: `${h}%`, opacity: 0.4 + i * 0.12 }} />
            ))}
          </span>
        </span>
      )
    case 'trend':
      return (
        <span className="flex h-full w-full flex-col px-3 pb-2 pt-2.5">
          <span className={CAPTION}>Brand compliance</span>
          <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="mt-2 w-full flex-1">
            <polyline points="0,40 18,34 34,36 50,24 66,20 82,12 100,8" fill="none" stroke="#00b81d" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
            <polyline points="0,40 18,34 34,36 50,24 66,20 82,12 100,8 100,50 0,50" fill="#00d722" fillOpacity="0.12" stroke="none" />
          </svg>
        </span>
      )
    case 'audit':
      return (
        <span className="flex h-full w-full flex-col justify-center gap-2 px-3">
          <span className={CAPTION}>Audit trail</span>
          {['Generated', 'Brand check', 'Approved'].map((step) => (
            <span key={step} className="flex items-center gap-1.5 text-[9px] text-[#0a0a0a]">
              <span className="flex h-3 w-3 shrink-0 items-center justify-center bg-[#00d722] text-[7px] font-bold leading-none text-white">✓</span>
              {step}
            </span>
          ))}
        </span>
      )
  }
}

/**
 * blue2purple-style directional list: full-width rows with a small index
 * hung on the title, a huge uppercase display title, and the description on
 * the right. On hover (fine pointers) a color band wipes down over the row,
 * the text flips to white and three straight product cards rise into the
 * reserved middle column. Fill and cards animate transform/opacity only.
 */
export function DirectionalList({ items, className }: { items: DirectionalItem[]; className?: string }) {
  return (
    <div className={cn('border-t border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]', className)}>
      {items.map((item, i) => (
        <ScrollReveal key={item.index} direction="none" delay={i * 0.06}>
          <Link
            to={item.href}
            className="group relative block overflow-hidden border-b border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]"
          >
            {/* Hover fill band: wipes down from the top (ease-in-out — travel). */}
            <span
              aria-hidden
              className={cn(
                'absolute inset-0 origin-top scale-y-0 transition-transform duration-300 ease-[cubic-bezier(0.76,0,0.24,1)]',
                '[@media(hover:hover)_and_(pointer:fine)]:group-hover:scale-y-100',
                FILL[item.color]
              )}
            />
            <span className="relative z-10 landing-container grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,34ch)] lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,30ch)] items-center gap-x-10 gap-y-2 py-8 md:py-10">
              {/* Index hung on the title's cap line, half its size. */}
              <span className="landing-text-display-xl flex items-start gap-[0.15em] text-[var(--color-landing-gray-900)] dark:text-white group-hover:text-white transition-colors duration-300">
                <span className="shrink-0 mt-[0.5em] text-[0.5em] leading-none font-[family-name:var(--font-landing-subdisplay)] font-light tracking-normal text-[var(--color-landing-gray-500)] group-hover:text-white/80 transition-colors duration-300 [font-feature-settings:'zero']">
                  {item.index}
                </span>
                <span className="min-w-0">{item.title}</span>
              </span>

              {/* Hover strip: straight product cards in a reserved column so
                  titles never run underneath; they rise and fade in, staggered. */}
              <span aria-hidden className="hidden lg:flex items-center gap-3 pointer-events-none">
                {(item.visuals ?? []).slice(0, 3).map((visual, v) => (
                  <span
                    key={v}
                    className="h-28 w-36 xl:w-40 overflow-hidden border border-black/10 bg-white shadow-[0_10px_30px_rgba(0,0,0,0.18)] opacity-0 translate-y-6 transition-[opacity,translate] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] [@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-100 [@media(hover:hover)_and_(pointer:fine)]:group-hover:translate-y-0"
                    style={{ transitionDelay: `${v * 70}ms` }}
                  >
                    <VisualContent visual={visual} />
                  </span>
                ))}
              </span>

              <span className="landing-text-body-sm text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)] group-hover:text-white/90 transition-colors duration-300 md:text-right">
                {item.description}
              </span>
            </span>
          </Link>
        </ScrollReveal>
      ))}
    </div>
  )
}

export default DirectionalList
